/**
 * IndexedDB каталог offline-регионов basemap (метаданные).
 * Тайлы — в Cache Storage `tactical-basemap-region-{id}`.
 */

export type BasemapRegion = {
  id: string;
  name: string;
  west: number;
  south: number;
  east: number;
  north: number;
  zMin: number;
  zMax: number;
  tileUrlTemplate: string;
  tileCount: number;
  createdAt: string;
};

const DB_NAME = "geopulseml-tactical-basemap";
const DB_VERSION = 1;
const STORE = "regions";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("idb open failed"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("idb tx error"));
    tx.onabort = () => reject(tx.error ?? new Error("idb tx abort"));
  });
}

export async function listBasemapRegions(): Promise<BasemapRegion[]> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readonly");
  const req = tx.objectStore(STORE).getAll();
  const rows = await new Promise<BasemapRegion[]>((resolve, reject) => {
    req.onsuccess = () => resolve((req.result as BasemapRegion[]) ?? []);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function putBasemapRegion(region: BasemapRegion): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).put(region);
  await txDone(tx);
  db.close();
}

export async function deleteBasemapRegionMeta(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).delete(id);
  await txDone(tx);
  db.close();
}

export function regionCacheName(id: string): string {
  return `tactical-basemap-region-${id}`;
}

export async function deleteBasemapRegionCache(id: string): Promise<void> {
  try {
    await caches.delete(regionCacheName(id));
  } catch {
    /* ignore */
  }
}
